import React, { useState, useMemo, useRef } from 'react';
import {
    Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, CircularProgress, IconButton, Snackbar, Alert, Box, Chip,
    FormControl, InputLabel, Select, MenuItem,
    Tooltip, TablePagination
} from '@mui/material';
import {
    Edit, Delete, Schedule, CheckCircle, Cancel, Warning,
    History as HistoryIcon, CloudUpload as UploadIcon,
    Note as NoteIcon, FileDownload,
    MonetizationOn as PaymentIcon,
    Print as PrintIcon
} from '@mui/icons-material';
import { appointmentAPI, reportAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import handlePrint from './appointmentsPrint';
import getStringValue from '../../constants/getStringValue';

const statusColors = {
    scheduled: 'primary',
    in_progress: 'warning',
    completed: 'success',
    cancelled: 'error',
    no_show: 'default',
};

const statusIcons = {
    scheduled: <Schedule />,
    in_progress: <Warning />,
    completed: <CheckCircle />,
    cancelled: <Cancel />,
    no_show: <Cancel />,
};




export const StatusChangeDialog = ({
    statusDialogOpen,
    setStatusDialogOpen,
    statusForm,
    setStatusForm,
    handleStatusSubmit,
    statusLoading,
    handleFileChange,
    t
}) => (
    <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('appointments.changeStatus')}</DialogTitle>
        <DialogContent>
            <Box sx={{ mt: 2 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{t('appointments.statusLabel')}</InputLabel>
                    <Select
                        value={statusForm.status || ''}
                        onChange={(e) => setStatusForm(prev => ({ ...prev, status: e.target.value }))}
                        label={t('appointments.statusLabel')}
                    >
                        <MenuItem value="" disabled>{t('appointments.selectStatus')}</MenuItem>
                        <MenuItem value="scheduled">{t('appointments.statusScheduled')}</MenuItem>
                        <MenuItem value="in_progress">{t('appointments.statusIn_progress')}</MenuItem>
                        <MenuItem value="completed">{t('appointments.statusCompleted')}</MenuItem>
                        <MenuItem value="cancelled">{t('appointments.statusCancelled')}</MenuItem>
                        <MenuItem value="no_show">{t('appointments.statusNo-show')}</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t('appointments.notes')}
                    value={statusForm.notes}
                    onChange={(e) => setStatusForm(prev => ({ ...prev, notes: e.target.value }))}
                    sx={{ mb: 2 }}
                />
                {statusForm.status === 'completed' && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>{t('appointments.uploadPDF')} *</Typography>
                        <input
                            accept="application/pdf"
                            style={{ display: 'none' }}
                            id="pdf-file-input"
                            type="file"
                            onChange={handleFileChange}
                        />
                        <label htmlFor="pdf-file-input">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<UploadIcon />}
                                fullWidth
                            >
                                {statusForm.pdfFile ? statusForm.pdfFile.name : t('appointments.selectPDF')}
                            </Button>
                        </label>
                        {statusForm.pdfFile && (
                            <Typography
                                variant="caption"
                                color="success.main"
                                display="block"
                                sx={{ mt: 1 }}
                            >
                                {t('appointments.pdfSelected')}: {statusForm.pdfFile.name}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setStatusDialogOpen(false)} disabled={statusLoading}>
                {t('common.cancel')}
            </Button>
            <Button
                onClick={handleStatusSubmit}
                variant="contained"
                disabled={statusLoading || (statusForm.status === 'completed' && !statusForm.pdfFile)}
            >
                {statusLoading ? <CircularProgress size={24} /> : t('common.update')}
            </Button>
        </DialogActions>
    </Dialog>
);

export const AppointmentsTable = ({
    appointments,
    isRTL,
    t,
    totals,
    fetchData,
    filters,
    daily = false,
    pagination,
    onChangePage,
    onChangeRowsPerPage,
    loading,
    onEdit,
}) => {
    const { user, isSuperAdmin } = useAuth();
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [statusForm, setStatusForm] = useState({ status: '', notes: '', pdfFile: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const navigate = useNavigate();

    const [paymentConfirmOpen, setPaymentConfirmOpen] = useState(false);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [selectedAppointmentForPayment, setSelectedAppointmentForPayment] = useState(null);

    //   State for printing
    const [appointmentToPrint, setAppointmentToPrint] = useState(null);
    const printRef = useRef(null);

    const canEdit = isSuperAdmin || user?.privileges?.some(p => p.module === 'appointments' && p.operations.includes('update'));
    const canDelete = isSuperAdmin || user?.privileges?.some(p => p.module === 'appointments' && p.operations.includes('delete'));
    const canChangePayment = canEdit;

    const handleEdit = (appointment) => onEdit(appointment);

    const handleStatusChange = (appointment) => {
        setSelectedAppointment(appointment);
        setStatusForm({
            status: (appointment.status && appointment.status !== 'undefined') ? appointment.status : '',
            notes: appointment.notes || '',
            pdfFile: null
        });
        setStatusDialogOpen(true);
    };

    const handleStatusSubmit = async () => {
        if (!selectedAppointment) return;
        setStatusLoading(true);
        try {
            const formData = new FormData();
            formData.append('status', statusForm.status);
            if (statusForm.notes) formData.append('notes', statusForm.notes);
            if (statusForm.pdfFile) formData.append('pdfFile', statusForm.pdfFile);

            await appointmentAPI.updateAppointmentStatus(selectedAppointment._id, formData, true);
            setSnackbar({
                open: true,
                message: t('appointments.statusUpdateSuccess'),
                severity: 'success'
            });
            setStatusDialogOpen(false);
            fetchData();
        } catch (err) {
            const errorMessage = err.response?.data?.message || t('appointments.statusUpdateError');
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        } finally {
            setStatusLoading(false);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) setStatusForm(prev => ({ ...prev, pdfFile: file }));
    };

    const handleOpenPaymentDialog = (appointment) => {
        setSelectedAppointmentForPayment(appointment);
        setPaymentConfirmOpen(true);
    };

    const handlePaymentConfirm = async () => {
        if (!selectedAppointmentForPayment) return;
        setPaymentLoading(true);
        try {
            const newStatus = !selectedAppointmentForPayment.isPaid;
            await appointmentAPI.updateAppointmentPaymentStatus(
                selectedAppointmentForPayment._id,
                { isPaid: newStatus }
            );
            setSnackbar({
                open: true,
                message: t('appointments.paymentStatusUpdateSuccess'),
                severity: 'success'
            });
            setPaymentConfirmOpen(false);
            fetchData();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.message || t('appointments.paymentStatusUpdateError'),
                severity: 'error'
            });
        } finally {
            setPaymentLoading(false);
            setSelectedAppointmentForPayment(null);
        }
    };



    const handleViewHistory = (id) => navigate(`/appointments/${id}/history`);

    const handleDeleteConfirm = async () => {
        if (deleteId) {
            try {
                await appointmentAPI.deleteAppointment(deleteId);
                setSnackbar({
                    open: true,
                    message: t('appointments.deleteSuccess'),
                    severity: 'success'
                });
                fetchData();
            } catch (err) {
                setSnackbar({
                    open: true,
                    message: err.response?.data?.message || t('appointments.deleteError'),
                    severity: 'error'
                });
            } finally {
                setDeleteId(null);
            }
        }
    };

    const handleGenerateReport = async (format) => {
        try {
            if (daily === true) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                filters.startDate = today.toISOString();
            }
            const response = await reportAPI.downloadExcelReport(filters);
            const filename = `appointments-report-${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
            const blob = new Blob(
                [response.data],
                { type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
            );
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.log("this is the error : ", error)
            setSnackbar({
                open: true,
                message: error.response?.statusText || t('reports.downloadError'),
                severity: 'error'
            });
        }
    };

    // Helper to determine if the payment button should be shown for a given appointment
    const canShowPaymentButton = (apt) => {
        if (!canChangePayment) return false;
        // Not paid → anyone with payment permission can mark as paid
        if (!apt.isPaid) return true;
        // Paid → only super admin can mark as unpaid
        if (apt.isPaid && isSuperAdmin) return true;
        // Paid + not super admin → hide button
        return false;
    };

    const columns = useMemo(() => {
        const isRepresentative = user?.userType === 'representative';

        // Base columns that representatives can see
        const baseColumns = [
            {
                id: 'branch',
                label: t('appointments.branch'),
                render: apt => getStringValue(apt.branch?.name)
            },
            {
                id: 'patient',
                label: t('appointments.patient'),
                render: apt => (
                    <Box>
                        <Typography variant="body2" fontWeight="bold">
                            {getStringValue(apt.patientId?.name)}
                        </Typography>
                        {!isRepresentative && (
                            <Typography variant="caption" color="textSecondary">
                                {apt.patientId?.phoneNumber}
                            </Typography>
                        )}
                    </Box>
                )
            },
            {
                id: 'doctorreferred',
                label: t('appointments.referredBy'),
                render: apt => (
                    <Box>
                        <Typography variant="body2" fontWeight="bold">
                            {getStringValue(apt.referredBy?.name)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {apt.referredBy?.specialization?.name}
                        </Typography>
                    </Box>
                )
            },
            {
                id: 'scans',
                label: t('navigation.scans'),
                render: apt => apt.scans?.map(s => `${getStringValue(s.scan?.name)} (x${s.quantity})`).join(', ')
            },
            {
                id: 'dateTime',
                label: t('appointments.dateTime'),
                render: apt => apt.scheduledAt ? new Date(apt.scheduledAt).toLocaleString() : ''
            },
        ];

        // If representative, return only base columns
        if (isRepresentative) {
            return baseColumns;
        }

        // Full columns for other user types
        return [
            baseColumns[0], // branch
            baseColumns[1], // patient
            {
                id: 'radiologist',
                label: t('appointments.radiologist'),
                render: apt => (
                    <Box>
                        <Typography variant="body2" fontWeight="bold">
                            {getStringValue(apt.radiologistId?.name)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {apt.radiologistId?.phoneNumber}
                        </Typography>
                    </Box>
                )
            },
            {
                id: 'technician',
                label: t('appointments.technician'),
                render: apt => (
                    <Box>
                        <Typography variant="body2" fontWeight="bold">
                            {getStringValue(apt.radiologyTechnician?.name)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {apt.radiologyTechnician?.phoneNumber}
                        </Typography>
                    </Box>
                )
            },
            baseColumns[3], // scans
            baseColumns[4], // dateTime
            {
                id: 'status',
                label: t('common.status'),
                render: apt => (
                    <Chip
                        icon={statusIcons[apt.status]}
                        label={t(`appointments.status${apt.status.charAt(0).toUpperCase() + apt.status.slice(1)}`)}
                        color={statusColors[apt.status] || 'default'}
                        size="small"
                    />
                )
            },
            {
                id: 'paymentStatus',
                label: t('appointments.paymentStatus'),
                render: apt => (
                    <Chip
                        label={t(apt.isPaid ? 'appointments.paid' : 'appointments.unpaid')}
                        color={apt.isPaid ? 'success' : 'error'}
                        size="small"
                        variant="outlined"
                    />
                )
            },
            ...(user?.userType !== 'radiologist' ? [{
                id: 'price',
                label: t('common.price'),
                render: apt => {
                    const displayPrice = apt.finalPrice
                        || apt.totalPrice
                        || (apt.makeHugeSale && apt.customPrice)
                        || ((apt.basePrice || apt.price || 0) - (apt.discountAmount || 0) + (apt.completionFee || 0));

                    return (
                        <>
                            <Typography variant="body2" fontWeight="bold">
                                {formatCurrency(displayPrice)}
                            </Typography>
                            {apt.makeHugeSale && (
                                <Chip
                                    sx={{ mt: 1 }}
                                    label={t('appointments.hugeSale')}
                                    color="secondary"
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            {apt.discountAmount > 0 && (
                                <Typography variant="caption" color="success.main" display="block">
                                    -{formatCurrency(apt.discountAmount)} {t('appointments.discount') || 'خصم'}
                                </Typography>
                            )}
                        </>
                    );
                }
            }] : []),
            baseColumns[2], // scans
            {
                id: 'actions',
                label: t('common.actions'),
                render: apt => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>

                        {/* Print button:
                            - Super admin: always visible (can print regardless of payment status)
                            - Others: only visible when appointment is paid */}
                        {(isSuperAdmin || apt.isPaid) && (
                            <Tooltip title={t('common.print') || 'طباعة'}>
                                <IconButton
                                    onClick={() => handlePrint(apt, t)}
                                    size="small"
                                    color="default"
                                >
                                    <PrintIcon />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Payment status button:
                            - Not paid: anyone with canChangePayment can mark as paid
                            - Paid: only super admin can mark as unpaid
                            - Paid + not super admin: button hidden */}
                        {canShowPaymentButton(apt) && (
                            <Tooltip title={t('appointments.changePaymentStatus')}>
                                <IconButton
                                    onClick={() => handleOpenPaymentDialog(apt)}
                                    size="small"
                                    color={apt.isPaid ? 'success' : 'error'}
                                >
                                    <PaymentIcon />
                                </IconButton>
                            </Tooltip>
                        )}

                        {canEdit && (
                            <Tooltip title={t('common.edit')}>
                                <IconButton onClick={() => handleEdit(apt)} size="small">
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={t('appointments.changeStatus')}>
                            <IconButton
                                onClick={() => handleStatusChange(apt)}
                                size="small"
                                color="primary"
                            >
                                <Schedule />
                            </IconButton>
                        </Tooltip>
                        {canDelete && (
                            <Tooltip title={t('common.delete')}>
                                <IconButton
                                    color="error"
                                    onClick={() => setDeleteId(apt._id)}
                                    size="small"
                                >
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                        )}
                        {apt.notes && (
                            <Tooltip title={apt.notes}>
                                <IconButton color="info" size="small">
                                    <NoteIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={t('appointments.viewHistory')}>
                            <IconButton
                                onClick={() => handleViewHistory(apt._id)}
                                size="small"
                            >
                                <HistoryIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        ];
    }, [t, user, canEdit, canDelete, canChangePayment, isSuperAdmin]);

    return (
        <Paper sx={{ width: '100%' }} >
            <TableContainer sx={{ overflowX: 'auto' }} dir={isRTL ? 'rtl' : 'ltr'}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {columns.map(col => (
                                <TableCell key={col.id}>{col.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : appointments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    {t('appointments.noAppointmentsFound')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            appointments.map(apt => (
                                <TableRow key={apt._id} hover>
                                    {columns.map(col => (
                                        <TableCell key={col.id}>
                                            {col.render(apt)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                component="div"
                count={pagination.total || 0}
                page={pagination.page}
                onPageChange={(event, newPage) => onChangePage(newPage)}
                rowsPerPage={pagination.limit || 10}
                onRowsPerPageChange={(event) => onChangeRowsPerPage(event.target.value)}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                labelRowsPerPage={t('common.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} ${t('common.of')} ${count}`
                }
            />

            {user?.userType !== 'radiologist' && user?.userType !== 'representative' && (
                <Paper sx={{ p: 2, mt: 2 }}>
                    <Typography variant="h6">{t('dashboard.totalsSummary')}</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('dashboard.totalRevenue')}</TableCell>
                                <TableCell>{t('dashboard.paidIncome')}</TableCell>
                                <TableCell>{t('dashboard.totalAppointments')}</TableCell>
                                <TableCell>{t('dashboard.totalPaidAppointments')}</TableCell>
                                <TableCell>{t('dashboard.totalPatients')}</TableCell>
                                <TableCell>{t('dashboard.totalScans')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    {formatCurrency(totals.totalIncome)}
                                </TableCell>
                                <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                    {formatCurrency(totals.totalPaidIncome)}
                                </TableCell>
                                <TableCell>{pagination.total || 0}</TableCell>
                                <TableCell>{totals.totalPaidAppointments || 0}</TableCell>
                                <TableCell>{totals.totalPatients || 0}</TableCell>
                                <TableCell>{totals.totalScans || 0}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Paper>
            )}

            {isSuperAdmin && (
                <Box sx={{ p: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<FileDownload />}
                        onClick={() => handleGenerateReport('excel')}
                    >
                        {t('common.exportExcel')}
                    </Button>
                </Box>
            )}

            <StatusChangeDialog
                statusDialogOpen={statusDialogOpen}
                setStatusDialogOpen={setStatusDialogOpen}
                statusForm={statusForm}
                setStatusForm={setStatusForm}
                handleStatusSubmit={handleStatusSubmit}
                statusLoading={statusLoading}
                handleFileChange={handleFileChange}
                t={t}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
                <DialogTitle>{t('appointments.deleteConfirmTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('appointments.deleteConfirmMessage')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteId(null)}>
                        {t('common.cancel')}
                    </Button>
                    <Button color="error" onClick={handleDeleteConfirm}>
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Payment Confirmation Dialog */}
            <Dialog open={paymentConfirmOpen} onClose={() => setPaymentConfirmOpen(false)}>
                <DialogTitle>{t('appointments.changePaymentStatus')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {selectedAppointmentForPayment?.isPaid
                            ? t('appointments.confirmMarkAsUnpaid')
                            : t('appointments.confirmMarkAsPaid')
                        }
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setPaymentConfirmOpen(false)}
                        disabled={paymentLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        color="primary"
                        onClick={handlePaymentConfirm}
                        disabled={paymentLoading}
                        variant="contained"
                    >
                        {paymentLoading ? <CircularProgress size={24} /> : t('common.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar(p => ({ ...p, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};